<template>
  <div v-if="campaign" class="usage-history-table-container">
    <mf-card-container
      v-model="search"
      title="Histórico de utilização - Cupom de desconto"
      description="Histórico de todas movimentações dos cupons de desconto dessa campanha."
    >
      <template v-slot:filters>
        <div class="d-flex flex-row align-center">
          <mf-status-filter v-if="itemsFilter.length > 1" :items="itemsFilter" @input="getFilterResult" />
          <div class="mr-4">
            <filters-menu :filters="['lojas', 'couponStatus']" default-date="desdeInicio" activator-type="button" />
          </div>
          <mf-search
            v-model="search"
            hide-details
            dense
            placeholder="Buscar por consumidor"
            :disabled="!hasPiiAccess"
            :tooltipMessage="!hasPiiAccess ? 'Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso' : ''"
          />
        </div>
      </template>

      <paginated-data-table
        :headers="headers"
        :search="search"
        :custom-items-per-page="10"
        class="mt-4"
        :query="query"
        :query-variables="queryVariables"
        query-name="benefitMovementHistory"
        query-main-property-name="benefitMovementHistory"
        :graphql-uri="$microservicesUrls['benefitsManager']"
        use-input-param
      >
        <template v-slot:[`item.status`]="{ item }">
          <mf-icon fill small :color="getStatusLabel(item.status).color">fiber_manual_record</mf-icon>
          {{ getStatusLabel(item.status).text }}
        </template>
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template v-slot:[`item.user.documentNo`]="{ item }">
          <mf-tooltip max-width="250" bottom content-class="truncated-tooltip" :disabled="!item.user || hasPiiAccess">
            <template v-slot:activator="{ on }">
              <a v-if="hasPiiAccess && item.user" target="_blank" class="link" :href="linkConsumer(item.user && item.user.documentNo)" v-on="on"
                >{{ item.user.documentNo }}
              </a>

              <span v-else v-on="on"> {{ item.user ? item.user.documentNo : '-' }}</span>
            </template>
            <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
          </mf-tooltip>
        </template>
        <template v-slot:[`item.user.name`]="{ item }">
          <mf-tooltip max-width="250" bottom content-class="truncated-tooltip" :disabled="!item.user || hasPiiAccess">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                {{ item.user ? item.user.name : '-' }}
              </span>
            </template>
            <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
          </mf-tooltip>
        </template>
        <template v-slot:[`item.couponId`]="{ item }">
          <mf-tooltip max-width="250" bottom content-class="truncated-tooltip" :disabled="!!item.couponId || item.status === 'CONSUMED'">
            <template v-slot:activator="{ on }">
              <span v-if="item.couponId" class="link" @click="openCouponDetails(item)" v-on="on">
                {{ item.couponId }}
              </span>
              <span v-else v-on="on">
                {{ item.status === 'CONSUMED' ? getConsumedStatusLabel(item.saleCouponStatus) : '-' }}
              </span>
            </template>
            <span>{{ `Cupons de desconto no status "${getStatusLabel(item.status).text}" não possuem vínculo com Cupom fiscal nem com Loja.` }}</span>
          </mf-tooltip>
        </template>
        <template v-slot:[`item.unit.name`]="{ item }">
          <mf-tooltip max-width="250" bottom content-class="truncated-tooltip" :disabled="item.unit && item.unit.name ? true : false">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                {{ item.unit && item.unit.name ? item.unit.name : '-' }}
              </span>
            </template>
            <span>{{ `Cupons de desconto no status "${getStatusLabel(item.status).text}" não possuem vínculo com Cupom fiscal nem com Loja.` }}</span>
          </mf-tooltip>
        </template>
      </paginated-data-table>
    </mf-card-container>

    <coupon-modal v-model="showCouponModal" :coupon="selectedCoupon" :client-id="userId" />
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { localeString } from '@/config/helpers.js'
import FiltersMenu from '@/components/FiltersMenu.vue'
import PaginatedDataTable from '@/components/PaginatedDataTable'
import { QUERY_BENEFIT_MOVEMENT_HISTORY } from '../../../graphql'
import { couponDiscount } from '../../../mixins/coupon-discount'
import { mapState } from 'vuex'
import CouponModal from '@/modules/clientes/detalhes-cliente/views/components/CouponModal.vue'

export default {
  name: 'UsageHistoryTable',
  components: {
    FiltersMenu,
    PaginatedDataTable,
    CouponModal,
  },
  mixins: [couponDiscount],
  props: {},
  data: () => ({
    benefitCampaign: null,
    selectedInternalItems: [],
    search: '',
    statusToSearch: [],
    options: {},
    rowsPerPageArray: [10, 25, 50, 100, { text: 'Todos', value: '' }],
    total: null,
    showCouponModal: false,
    selectedCoupon: {},
    userId: '',
  }),
  computed: {
    ...mapState({ stateFilters: (state) => state.filters }),
    campaign: {
      get() {
        return this.benefitCampaign
      },
      set(value) {
        this.benefitCampaign = value
      },
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
    query() {
      return QUERY_BENEFIT_MOVEMENT_HISTORY
    },
    parsedStatus() {
      return this.statusToSearch ? this.statusToSearch : []
    },
    queryVariables() {
      return {
        status: this.parsedStatus,
        franchise: this.isFranchise || this.isFranchisee,
        id: this.campaign._id,
        units: this.stateFilters.units,
        saleCouponStatus: this.stateFilters.couponStatus,
      }
    },
    itemsFilter() {
      return [
        { label: this.getStatusLabel('CONSUMED').text, value: 'CONSUMED', color: this.getStatusLabel('CONSUMED').color, active: true },
        { label: this.getStatusLabel('ACTIVATED').text, value: 'ACTIVATED', color: this.getStatusLabel('ACTIVATED').color, active: true },
        { label: this.getStatusLabel('REDEEMED').text, value: 'REDEEMED', color: this.getStatusLabel('REDEEMED').color, active: true },
      ]
    },
    headers() {
      const headers = [
        {
          text: 'Status',
          value: 'status',
          width: '10%',
        },
        {
          text: 'Data e hora',
          value: 'createdAt',
          width: '15%',
        },
        {
          text: 'Consumidor',
          value: 'user.documentNo',
          width: '15%',
        },
        {
          text: 'Nome consumidor',
          value: 'user.name',
          width: '15%',
        },
        {
          text: 'ID Cupom fiscal',
          value: 'couponId',
          width: '20%',
        },
        {
          text: 'Loja',
          value: 'unit.name',
          width: '25%',
          sortable: false,
        },
      ]
      return headers
    },
  },
  mounted() {
    this.campaign = this.benefit
  },
  methods: {
    localeString,
    openCouponDetails(coupon) {
      this.selectedCoupon = { ...coupon, id: coupon.couponId }
      this.userId = coupon.user._id
      this.showCouponModal = true
    },
    formatDate(date) {
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy | HH:mm')
    },
    getStatusLabel(key) {
      const status = {
        REDEEMED: {
          text: 'Na carteira',
          color: '#F36600',
        },
        CONSUMED: {
          text: 'Consumido',
          color: '#0079F2',
        },
        ACTIVATED: {
          text: 'Ativado',
          color: '#0A8533',
        },
      }
      return key ? status[key] : { text: '', color: '' }
    },
    getConsumedStatusLabel(key) {
      const status = {
        RECOVERED: 'Recuperado',
        NOT_RECOVERED: 'Não recuperado',
        NOT_PROCESSED: 'Ainda não gerado',
      }
      return key ? status[key] : ''
    },
    setCurrencyValue(value) {
      return `R$ ${this.localeString(value, 2)}`
    },
    setPercentageValue(value) {
      return `${value}%`
    },
    getFilterResult(value) {
      this.statusToSearch = value.length ? value : this.itemsFilter.map((item) => item.value)
    },
    linkConsumer(param) {
      return `#/clientes/detalhes-cliente/${param}`
    },
  },
}
</script>
